import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ClientLayout from "../../../components/client/ClientLayout";
import { clientApi } from "../../../api";
import { formatDateAU } from "../../../utils/formatDate";

/**
 * IGT Update Profile — staff-initiated edits to an existing member.
 *
 * Flow:
 *   1. Load step — staff types a membership number (or arrives with
 *      ?id=... from the Member Detail page) and loads the profile.
 *   2. Edit step — four sections (details / address / email / phone).
 *      Only fields the user actually modifies get sent back on save.
 *      Single "Save changes" button hits one endpoint which fans out
 *      the PUTs. Per-section outcomes are shown after save — partial
 *      success is visible (e.g. "Details saved, phone failed").
 *
 * The backend (/api/client/igt/update-profile) already falls back
 * from PUT to POST when a sub-resource doesn't exist yet, so adding
 * a first email / phone / address to a member with none works too.
 */
export default function IgtUpdateProfile() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const idFromUrl = params.get("id") || "";

  const [memberNumber, setMemberNumber] = useState(idFromUrl);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [loaded, setLoaded]     = useState(null);      // original profile
  const [form, setForm]         = useState(null);      // edited draft
  const [results, setResults]   = useState(null);      // per-section save results
  const [refData, setRefData]   = useState({ titles: [], genders: [] });

  // Pull titles + genders once for the dropdowns.
  useEffect(() => {
    clientApi.igt.getRefData().then(setRefData).catch(() => {});
  }, []);

  // Resolve the member's title/gender to the numeric ids the dropdowns use,
  // once both ref-data and the loaded member are available. Runs once per
  // load (guarded on a `_resolved` flag) and stamps the resolved ids onto
  // `loaded` too, so the change-detector compares id-to-id and doesn't flag
  // an unchanged title/gender as edited.
  //
  // Title preference: a numeric titleId supplied by find-member (sourced
  // from our local row when the live IGT profile didn't return a title) wins;
  // otherwise we match the live title NAME ("MR.") against ref-data. Gender
  // resolves independently so a known title never blocks gender resolution.
  useEffect(() => {
    if (!loaded || loaded._resolved) return;
    if (!refData.titles?.length && !refData.genders?.length) return;
    // Match on name, tolerant of trailing-period / case differences
    // ("MR." from the profile vs "Mr" in ref-data, "Male" vs "male").
    const norm = (s) => String(s || "").trim().toLowerCase().replace(/\.+$/, "");
    const eq = (a, b) => norm(a) === norm(b);
    // Prefer the live title NAME (freshest — reflects a just-saved change);
    // fall back to the numeric titleId from our local row only when the live
    // profile didn't return a title.
    let titleId = "";
    if (loaded.title) {
      const t = refData.titles.find((x) => eq(x.name, loaded.title));
      if (t) titleId = String(t.title_id);
    }
    if (!titleId && loaded.titleId) titleId = String(loaded.titleId);
    let genderId = loaded.genderId || "";
    if (!genderId && loaded.gender) {
      const g = refData.genders.find((x) => eq(x.name, loaded.gender));
      if (g) genderId = g.gender_id;
    }
    setLoaded((m) => (m ? { ...m, titleId, genderId, _resolved: true } : m));
    setForm((f) => (f ? { ...f, titleId: f.titleId || titleId, genderId: f.genderId || genderId } : f));
    // eslint-disable-next-line
  }, [refData, loaded]);

  // If we arrived with ?id=..., auto-load.
  // eslint-disable-next-line
  useEffect(() => { if (idFromUrl && !loaded) loadMember(idFromUrl); }, [idFromUrl]);

  async function loadMember(num) {
    const n = String(num || "").trim();
    if (!n) return;
    setLoading(true); setError(""); setLoaded(null); setForm(null); setResults(null);
    try {
      const r = await clientApi.igt.findMember({ memberNumber: n });
      if (!r.found) { setError(`No member found with number ${n}.`); setLoading(false); return; }
      const m = r.member;
      setLoaded(m);
      setForm({
        // Details
        firstName:     m.firstName     || "",
        lastName:      m.lastName      || "",
        preferredName: m.preferredName || "",
        dob:           m.dateOfBirth ? String(m.dateOfBirth).slice(0, 10) : "",
        // Title/gender come back from the profile GET as NAMES ("MR." /
        // "Male"); they're resolved to the dropdown ids by the effect
        // below once ref-data is available.
        genderId:      "",
        titleId:       "",
        // Address
        line1:    m.address?.line1    || "",
        line2:    m.address?.line2    || "",
        city:     m.address?.city     || "",
        state:    m.address?.state    || "",
        zipCode:  m.address?.postcode || "",
        // Contact
        email:     m.email     || "",
        mobile:    m.mobile    || "",
        telephone: m.telephone || "",
      });
      // Persist the id in the URL so refresh / back stays on the same member.
      if (memberNumber !== n) setMemberNumber(n);
      if (idFromUrl !== n)    setParams({ id: n });
    } catch (err) {
      setError(err?.message || "Couldn't load member");
    }
    setLoading(false);
  }

  // Build the minimal payload — only sections whose fields changed.
  const changedPayload = useMemo(() => {
    if (!loaded || !form) return null;
    const payload = { membershipNumber: loaded.membershipNumber };

    const details = {};
    if (form.firstName     !== (loaded.firstName     || "")) details.firstName     = form.firstName;
    if (form.lastName      !== (loaded.lastName      || "")) details.lastName      = form.lastName;
    if (form.preferredName !== (loaded.preferredName || "")) details.preferredName = form.preferredName;
    if (form.dob           !== (loaded.dateOfBirth ? String(loaded.dateOfBirth).slice(0, 10) : "")) details.dob = form.dob;
    // Compare against the resolved baseline ids (set by the resolver
    // effect), not the profile's name strings, so an untouched title/
    // gender isn't reported as changed. Only send when non-empty.
    if (form.genderId && form.genderId !== (loaded.genderId || "")) details.genderId = form.genderId;
    if (form.titleId  && form.titleId  !== (loaded.titleId  || "")) details.title    = String(form.titleId);
    if (Object.keys(details).length > 0) payload.details = details;

    const address = {};
    if (form.line1   !== (loaded.address?.line1    || "")) address.line1   = form.line1   || "";
    if (form.line2   !== (loaded.address?.line2    || "")) address.line2   = form.line2   || "";
    if (form.city    !== (loaded.address?.city     || "")) address.city    = form.city    || "";
    if (form.state   !== (loaded.address?.state    || "")) address.state   = form.state   || "";
    if (form.zipCode !== (loaded.address?.postcode || "")) address.zipCode = form.zipCode || "";
    if (Object.keys(address).length > 0) payload.address = address;

    if (form.email     !== (loaded.email     || "")) payload.email     = form.email;
    if (form.mobile    !== (loaded.mobile    || "")) payload.mobile    = form.mobile;
    if (form.telephone !== (loaded.telephone || "")) payload.telephone = form.telephone;

    return payload;
  }, [loaded, form]);

  const hasChanges = changedPayload
    && Object.keys(changedPayload).some((k) => k !== "membershipNumber");

  async function save() {
    if (!hasChanges) return;
    setSaving(true); setError(""); setResults(null);
    try {
      const r = await clientApi.igt.updateProfile(changedPayload);
      setResults(r.results);
      // Re-seed loaded + form with the server's post-save state.
      if (r.member) {
        setLoaded(r.member);
        setForm((f) => ({
          ...f,
          firstName:     r.member.firstName     || "",
          lastName:      r.member.lastName      || "",
          preferredName: r.member.preferredName || "",
          dob:           r.member.dateOfBirth ? String(r.member.dateOfBirth).slice(0, 10) : "",
          // Cleared so the resolver effect re-maps the returned gender
          // NAME to its id (setLoaded(r.member) re-arms that effect).
          genderId:      "",
          line1:    r.member.address?.line1    || "",
          line2:    r.member.address?.line2    || "",
          city:     r.member.address?.city     || "",
          state:    r.member.address?.state    || "",
          zipCode:  r.member.address?.postcode || "",
          email:     r.member.email     || "",
          mobile:    r.member.mobile    || "",
          telephone: r.member.telephone || "",
        }));
      }
    } catch (err) {
      setError(err?.message || "Save failed");
    }
    setSaving(false);
  }

  const setF = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Update profile</h1>
          <p className="page-subtitle">IGT — edit an existing member's details, address and contact info</p>
        </div>
        {loaded?.membershipNumber && (
          <button className="btn btn-outline" onClick={() => navigate(`/client/members/${loaded.membershipNumber}`)}>
            Open full profile →
          </button>
        )}
      </div>

      {error && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}

      {/* Load step — always visible at the top so staff can switch members. */}
      <form
        className="settings-card"
        style={{ marginBottom: "1rem" }}
        onSubmit={(e) => { e.preventDefault(); loadMember(memberNumber); }}
      >
        <div className="settings-card-header">
          <h3>Load a member</h3>
          <p>Enter the membership number. You can also open this tool from a member's profile page.</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end" }}>
          <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
            <label>Membership number</label>
            <input
              value={memberNumber}
              onChange={(e) => setMemberNumber(e.target.value.replace(/\s+/g, ""))}
              placeholder="e.g. 56153"
              inputMode="numeric"
              autoFocus={!idFromUrl}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading || !memberNumber.trim()}>
            {loading ? "Loading…" : "Load"}
          </button>
        </div>
      </form>

      {/* Edit step — only renders once a member is loaded. */}
      {loaded && form && (
        <>
          {results && <ResultsBanner results={results} />}

          <Section title="Personal details">
            <div className="form-grid-2">
              <Field label="First name"     value={form.firstName}     onChange={setF("firstName")} />
              <Field label="Last name"      value={form.lastName}      onChange={setF("lastName")} />
              <Field label="Preferred name" value={form.preferredName} onChange={setF("preferredName")} />
              <Field label="Date of birth"  value={form.dob}           onChange={setF("dob")} type="date" />
              <SelectField
                label="Gender"
                value={form.genderId}
                onChange={setF("genderId")}
                options={[{ value: "", label: "—" }, ...refData.genders.map((g) => ({ value: g.gender_id, label: g.name }))]}
              />
              <SelectField
                label="Title"
                value={form.titleId}
                onChange={setF("titleId")}
                options={[{ value: "", label: "—" }, ...refData.titles.map((t) => ({ value: String(t.title_id), label: t.name }))]}
              />
            </div>
          </Section>

          <Section title="Address">
            <div className="form-grid-2">
              <Field label="Line 1"   value={form.line1}   onChange={setF("line1")} full />
              <Field label="Line 2"   value={form.line2}   onChange={setF("line2")} full />
              <Field label="Suburb"   value={form.city}    onChange={setF("city")} />
              <Field label="State"    value={form.state}   onChange={setF("state")} />
              <Field label="Postcode" value={form.zipCode} onChange={setF("zipCode")} />
            </div>
          </Section>

          <Section title="Contact">
            <div className="form-grid-2">
              <Field label="Email"     value={form.email}     onChange={setF("email")} type="email" />
              <Field label="Mobile"    value={form.mobile}    onChange={setF("mobile")} inputMode="tel" />
              <Field label="Telephone" value={form.telephone} onChange={setF("telephone")} inputMode="tel" />
            </div>
          </Section>

          {/* Membership context — shown so this screen matches the full
              profile view. IGT has no update endpoint for these, so they're
              read-only here. */}
          <Section title="Membership details">
            <div className="form-grid-2">
              <ReadonlyField label="Occupation"      value={loaded.occupation} />
              <ReadonlyField label="Date of joining" value={fmtDate(loaded.dateOfJoining)} />
              <ReadonlyField label="Card number"     value={loaded.cardNumber} />
              <ReadonlyField label="Card expiry"     value={fmtDate(loaded.cardExpiry)} />
            </div>
            <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "var(--text-muted)" }}>
              These fields are read-only — IGT has no update endpoint for them.
            </p>
          </Section>

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
            <button className="btn btn-primary" disabled={!hasChanges || saving} onClick={save}>
              {saving ? "Saving…" : hasChanges ? "Save changes" : "No changes"}
            </button>
            <button className="btn btn-outline" disabled={saving} onClick={() => loadMember(loaded.membershipNumber)}>
              Reload from server
            </button>
          </div>
        </>
      )}
    </ClientLayout>
  );
}

/* ── Helpers ─────────────────────────────────────────── */
function Section({ title, children }) {
  return (
    <div className="settings-card" style={{ marginBottom: "1rem" }}>
      <div className="settings-card-header">
        <h3>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", full = false, inputMode, ...rest }) {
  return (
    <div className="form-field" style={full ? { gridColumn: "1 / -1" } : undefined}>
      <label>{label}</label>
      <input type={type} value={value || ""} onChange={onChange} inputMode={inputMode} {...rest} />
    </div>
  );
}

function ReadonlyField({ label, value }) {
  return (
    <div className="form-field">
      <label>{label}</label>
      <input value={value || "—"} readOnly disabled />
    </div>
  );
}

function fmtDate(v) {
  return formatDateAU(v, "");
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div className="form-field">
      <label>{label}</label>
      <select value={value || ""} onChange={onChange}>
        {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function ResultsBanner({ results }) {
  const entries = Object.entries(results);
  if (entries.length === 0) return null;
  const allOk   = entries.every(([, r]) => r.ok || r.skipped);
  const failed  = entries.filter(([, r]) => !r.ok && !r.skipped);
  return (
    <div className={`toast ${allOk ? "toast-success" : "toast-error"}`} style={{ marginBottom: "1rem" }}>
      {allOk ? "✓ Saved." : "⚠ Some changes didn't save."}
      {!allOk && (
        <ul style={{ margin: "0.4rem 0 0 1rem", fontSize: "0.85rem" }}>
          {failed.map(([k, r]) => <li key={k}><strong>{k}</strong>: {r.error}</li>)}
        </ul>
      )}
    </div>
  );
}
